import apiClient, { artworkCmd } from './apiClient';
import { readClip } from '../canvas/clips';
import { capSentence, overCap } from './library';
import { prepareImage } from './imageUpload';
import { putStorage, type UploadProgress } from './storageUpload';

export interface PlacedPicture {
  readonly mediaAssetId: string;
  /** Where the canvas loads the bytes from. */
  readonly url: string;
  readonly width: number;
  readonly height: number;
  readonly mime: string;
  readonly name: string;
  /** A video's length in seconds: present, it goes on the page as a clip. */
  readonly duration?: number;
  /** Present only when the uploaded image was actually reduced in pixel dimensions. */
  readonly downsizedFrom?: { width: number; height: number };
}

/** The videos a clip may be: what the browser plays and the render service decodes alike. */
const CLIP_TYPES = new Set(['video/mp4', 'video/webm', 'video/quicktime', 'video/x-m4v']);

/** Whether a file is a video, to be placed as a clip rather than a picture. */
export function isClipFile(file: File): boolean {
  return file.type.startsWith('video/');
}

interface Presigned {
  uploadUrl: string;
  storageRef: string;
  storagePath: string;
  headers?: Record<string, string>;
}

/**
 * Where the person's own upload is kept: in Flobi Drive, like every other upload in Flobi
 * — in the project's Drive folder when the file is in a project, else at Drive's top level.
 * Given only for what the person chose (Upload, a drop, a paste, Replace). A picture the
 * editor made itself (Rasterize) or one taken out of an imported SVG is the file's alone
 * and stays out of Drive.
 */
export interface IntoDrive {
  readonly projectId: string | null;
}

/**
 * Puts a picture or a video where Flobi keeps media and answers what the canvas needs.
 *
 * With `into`, through Flobi Drive's own direct upload (Tarek, 2026-10-01: "why aren't we
 * using the same upload endpoints we have?"), so it is a Drive file in the workspace,
 * listed in My Stuff and in Drive, with the previews media makes for every upload. The
 * Artwork backend's own presign filed it under the organisation's id rather than the
 * workspace's, so nothing uploaded in Artwork ever showed in either. Without `into`, or
 * for a seat whose plan leaves Drive out, that route is still how a file gets its media.
 *
 * The bytes never touch the gateway or the Artwork backend either way. A document ends up
 * holding an asset id and a URL — never the picture itself, which would put megabytes
 * into a document that is re-sent on every edit.
 */
export async function uploadPicture(documentId: string, file: File, into?: IntoDrive, onProgress?: (progress: UploadProgress) => void): Promise<PlacedPicture> {
  const [one] = await uploadPictures(documentId, [file], into, undefined, onProgress ? (_file, progress) => onProgress(progress) : undefined);
  if (one?.status !== 'fulfilled') throw one?.reason;
  return one.value;
}

/** Video references use Drive storage; a reference is not subject to canvas clip limits. */
export async function uploadVideoReference(file: File, into: IntoDrive, onProgress?: (progress: UploadProgress) => void): Promise<PlacedPicture> {
  if (!isClipFile(file)) throw Error('Choose an MP4, MOV or WebM video.');
  const upload = new DriveUpload(into, file.size);
  try {
    return await inTurn(async turn => {
      onProgress?.({ phase: 'preparing' });
      const url = URL.createObjectURL(file);
      let info: Awaited<ReturnType<typeof readClip>>;
      try { info = await readClip(url); } finally { URL.revokeObjectURL(url); }
      turn.signal.throwIfAborted(); turn.moved();
      const asset = await intoDrive(file, file.name, file.type, upload, { ...turn, progress: onProgress });
      return { ...asset, name: file.name, mime: file.type, ...info };
    }, patienceFor(file));
  } finally { upload.close(); }
}

/** Generation audio uses the same private Drive upload and storage progress as pictures. */
export async function uploadAudioReference(file: File, into: IntoDrive, onProgress?: (progress: UploadProgress) => void): Promise<PlacedPicture> {
  if (!file.type.startsWith('audio/')) throw Error('Choose an audio file.');
  const upload = new DriveUpload(into, file.size);
  try {
    return await inTurn(async turn => {
      onProgress?.({ phase: 'preparing' });
      const duration = await readAudioDuration(file, turn.signal);
      turn.signal.throwIfAborted(); turn.moved();
      const asset = await intoDrive(file, file.name, file.type, upload, { ...turn, progress: onProgress });
      return { ...asset, name: file.name, mime: file.type, width: 0, height: 0, duration };
    }, patienceFor(file));
  } finally { upload.close(); }
}

export function readAudioDuration(file: Blob, signal?: AbortSignal): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file), audio = new Audio();
    const finish = (duration?: number, problem?: Error) => {
      clearTimeout(timer); signal?.removeEventListener('abort', aborted);
      audio.onloadedmetadata = null; audio.onerror = null; audio.src = ''; URL.revokeObjectURL(url);
      if (problem) reject(problem); else resolve(duration!);
    };
    const aborted = () => finish(undefined, new Error('Audio preparation was canceled.'));
    const timer = setTimeout(() => finish(undefined, new Error('This audio file could not be read. Try a WAV, MP3 or M4A.')), 15_000);
    audio.onloadedmetadata = () => Number.isFinite(audio.duration) && audio.duration > 0 ? finish(audio.duration) : finish(undefined, new Error('This audio file has no readable duration.'));
    audio.onerror = () => finish(undefined, new Error('This audio file could not be opened. Try a WAV, MP3 or M4A.'));
    if (signal?.aborted) { aborted(); return; }
    signal?.addEventListener('abort', aborted, { once: true }); audio.preload = 'metadata'; audio.src = url;
  });
}

/**
 * Many files the way Drive's own app sends a selection: one Drive upload for all of them,
 * a few files at a time, closed once when the last is in. 160 pictures dropped together
 * used to go up all at once, each its own Drive upload: 160 Drive transactions queued on
 * one folder's lock, Drive's database pool ran dry, and Drive and the gateway failed for
 * everyone while most of the pictures never arrived (Tarek, 2026-10-03). Each file's
 * outcome is its own, in the order given.
 */
export async function uploadPictures(documentId: string, files: readonly File[], into?: IntoDrive, onSettled?: () => void, onProgress?: (file: File, progress: UploadProgress) => void): Promise<PromiseSettledResult<PlacedPicture>[]> {
  // Image sizes change during preparation. Drive checks the actual prepared bytes for
  // each file; quoting the original total could reject a resized upload that would fit.
  const totalBytes = files.every(isClipFile) ? files.reduce((sum, file) => sum + file.size, 0) : undefined;
  const upload = into ? new DriveUpload(into, totalBytes) : undefined;
  try {
    return await Promise.allSettled(
      files.map((file) => {
        const one = inTurn((turn) => uploadOne(documentId, file, upload, { ...turn, progress: onProgress ? progress => onProgress(file, progress) : undefined }), patienceFor(file));
        // Told as each one lands or fails, so whoever is waiting can say how far it has got.
        if (onSettled) void one.then(onSettled, onSettled);
        return one;
      }),
    );
  } finally {
    upload?.close();
  }
}

/** Files going up at once, across the whole editor: Drive's own app sends 3 (its MAX_PARALLEL_BATCHES). */
const AT_ONCE = 3;
let going = 0;
const queued: (() => void)[] = [];

/** What a file going up is handed: the signal its requests carry, and `moved()` to say it is getting somewhere. */
interface Turn {
  readonly signal: AbortSignal;
  readonly moved: () => void;
  readonly progress?: (progress: UploadProgress) => void;
}

/** A file that stops moving is given up, so it cannot hold its slot until a reload. */
export class UploadStalled extends Error {
  constructor() {
    super('The upload stopped moving. Check your connection and try again.');
    this.name = 'UploadStalled';
  }
}

/**
 * How long a file may go without moving. A PUT reports nothing until it lands, so the wait
 * covers a whole file at a slow 100 KB/s, on top of a minute; each 5 MB part that lands
 * starts it again.
 */
function patienceFor(file: File): number {
  return 60_000 + file.size / 100;
}

/**
 * Runs `work` once fewer than AT_ONCE files are going up, so a big drop waits its turn rather
 * than all going at once. A PUT whose connection died (Wi-Fi changed, the laptop slept) never
 * settles on its own, and three of them used to hold every slot until a reload: after
 * `stallMs` with no `moved()`, the file's requests are aborted and its slot passes on.
 */
async function inTurn<T>(work: (turn: Turn) => Promise<T>, stallMs: number): Promise<T> {
  if (going >= AT_ONCE) await new Promise<void>((resolve) => queued.push(resolve));
  else going += 1;
  const controller = new AbortController();
  let settled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let giveUp: (reason: unknown) => void = () => undefined;
  const gaveUp = new Promise<never>((_, reject) => {
    giveUp = reject;
  });
  const moved = () => {
    if (settled) return;
    clearTimeout(timer);
    timer = setTimeout(() => {
      const why = new UploadStalled();
      controller.abort(why);
      giveUp(why);
    }, stallMs);
  };
  moved();
  try {
    return await Promise.race([work({ signal: controller.signal, moved }), gaveUp]);
  } finally {
    settled = true;
    clearTimeout(timer);
    // The slot passes straight to the next one waiting, or is given back.
    const next = queued.shift();
    if (next) next();
    else going -= 1;
  }
}

async function uploadOne(documentId: string, file: File, into: DriveUpload | undefined, turn: Turn): Promise<PlacedPicture> {
  turn.progress?.({ phase: 'preparing' });
  if (isClipFile(file)) return uploadClip(documentId, file, into, turn);
  const prepared = await prepareImage(file);
  turn.signal.throwIfAborted();
  turn.moved();
  const mime = prepared.blob.type || file.type;
  turn.progress?.({ phase: 'uploading', loaded: 0, total: prepared.blob.size });
  const name = prepared.blob === file ? file.name || 'Picture' : renamed(file.name || 'Picture', mime);
  const asset = await keepMedia(documentId, prepared.blob, name, mime, into, turn);
  return { mediaAssetId: asset.mediaAssetId, url: asset.url, width: prepared.width, height: prepared.height, mime, name: file.name || 'Picture', ...(prepared.downsizedFrom ? { downsizedFrom: prepared.downsizedFrom } : {}) };
}

/**
 * A video, uploaded as it is — never re-encoded in the browser — once it is known to be one
 * a page will take: a kind the browser and the render service both play, and within the
 * cap Tarek set (`VIDEO_CAP`). Its length and size are read from the file before a byte
 * goes up, so a clip over the cap costs nothing to refuse.
 */
async function uploadClip(documentId: string, file: File, into: DriveUpload | undefined, turn: Turn): Promise<PlacedPicture> {
  const name = file.name || 'Video';
  if (!CLIP_TYPES.has(file.type)) throw new Error(`${name} is a kind of video Artwork does not play. Try an MP4, MOV or WebM.`);
  const big = overCap({ kind: 'video', bytes: file.size });
  if (big) throw new Error(capSentence(name, big));
  const local = URL.createObjectURL(file);
  let facts: Awaited<ReturnType<typeof readClip>>;
  try {
    facts = await readClip(local);
  } catch {
    throw new Error(`${name} could not be opened. Try an MP4 made with H.264.`);
  } finally {
    URL.revokeObjectURL(local);
  }
  const long = overCap({ kind: 'video', duration: facts.duration });
  if (long) throw new Error(capSentence(name, long));
  turn.signal.throwIfAborted();
  turn.moved();
  const asset = await keepMedia(documentId, file, name, file.type, into, turn);
  return { mediaAssetId: asset.mediaAssetId, url: asset.url, width: facts.width, height: facts.height, mime: file.type, name, duration: Math.round(facts.duration * 1000) / 1000 };
}

/** Drive when it is asked for and the seat has it; the file's own media otherwise. */
async function keepMedia(documentId: string, blob: Blob, name: string, mime: string, into: DriveUpload | undefined, turn: Turn): Promise<{ mediaAssetId: string; url: string }> {
  if (into) {
    try {
      return await intoDrive(blob, name, mime, into, turn);
    } catch (error) {
      if (!(error instanceof NoDrive)) throw error;
    }
  }
  return store(documentId, blob, name, mime, turn);
}

/** The gateway turned Drive's routes away: this seat's plan does not include it. */
class NoDrive extends Error {}

/** One of Drive's commands, through the gateway; its own words when it refuses. */
async function drive<T>(action: string, body: Record<string, unknown>): Promise<T> {
  try {
    const response = await apiClient.post(`/drive/${action}`, body);
    const answer = response?.data as { success?: boolean; data?: T; error?: unknown; message?: string } | undefined;
    if (answer && answer.success === false) throw new Error(refusalOf(answer) ?? 'Flobi Drive refused the upload.');
    return (answer && typeof answer === 'object' && 'data' in answer ? answer.data : answer) as T;
  } catch (error) {
    const response = (error as { response?: { status?: number; data?: unknown } }).response;
    if (response?.status === 403) throw new NoDrive('Your seat does not include Flobi Drive.');
    if (response) throw new Error(refusalOf(response.data) ?? 'Flobi Drive could not be reached.');
    throw error;
  }
}

function refusalOf(answer: unknown): string | null {
  const one = (answer ?? {}) as { message?: unknown; error?: unknown };
  if (typeof one.message === 'string' && one.message) return one.message;
  if (typeof one.error === 'string' && one.error) return one.error;
  const inner = one.error as { message?: unknown } | undefined;
  return typeof inner?.message === 'string' ? inner.message : null;
}

/** Drive's part size (its directUploadService CHUNK_SIZE). */
const CHUNK = 5 * 1024 * 1024;
/** Parts in flight at once, and tries per part — as Drive's own app sends them. */
const PARALLEL = 6;
const TRIES = 3;

interface PartPlan {
  readonly partNumber: number;
  readonly uploadUrl: string;
  readonly start: number;
  readonly end: number;
}

/**
 * One Drive upload shared by every file going up together, as Drive's own app shares one
 * across a selection. Started by the first file to go, with the folder and a byte total
 * when it is known. Resized images use Drive's per-file quota check on the prepared
 * bytes instead. Closed
 * (`completeAll`) once, after the last. Drive refuses to close one while files of the
 * person's are still going up, so a file closing its own upload while others went up
 * beside it was refused every time. Closing is not waited for; Drive closes one left open
 * when it expires.
 */
class DriveUpload {
  private started: Promise<string> | null = null;

  constructor(
    private readonly into: IntoDrive,
    private readonly totalBytes: number | undefined,
  ) {}

  /** The upload's id, started on first ask; a refusal (or a seat without Drive) is every file's. */
  id(): Promise<string> {
    this.started ??= this.start();
    return this.started;
  }

  close(): void {
    this.started?.then((uploadId) => drive('drive.upload.direct.completeAll', { uploadId })).catch(() => undefined);
  }

  private async start(): Promise<string> {
    const folderId = this.into.projectId ? await projectFolder(this.into.projectId) : null;
    const started = await drive<{ uploadId?: string }>('drive.upload.direct.start', { ...(folderId ? { folderId } : {}), isPublic: false, ...(this.totalBytes !== undefined ? { totalBytes: this.totalBytes } : {}) });
    if (!started?.uploadId) throw new Error('Flobi Drive did not start the upload.');
    return started.uploadId;
  }
}

/**
 * Into Flobi Drive the way Drive's own app and Projects' Files tab upload
 * (`drive.upload.direct.*`), one file at a time within the shared upload: plan the file's
 * parts, PUT each straight to storage, and complete, which makes the Drive file and its
 * media record (answered with them since flobi-drive-back's `completeBatch` says so).
 */
async function intoDrive(blob: Blob, name: string, mime: string, upload: DriveUpload, turn: Turn): Promise<{ mediaAssetId: string; url: string }> {
  const uploadId = await upload.id();
  turn.moved();
  const batch = await drive<{ batchId?: string; files?: { sessionId?: string; parts?: PartPlan[] }[] }>('drive.upload.direct.initiateBatch', {
    uploadId,
    chunkSize: CHUNK,
    files: [{ name, size: blob.size, mimeType: mime }],
  });
  const plan = batch?.files?.[0];
  if (!batch?.batchId || !plan?.sessionId || !plan.parts?.length) throw new Error(`Flobi Drive would not take ${name}.`);
  let parts: { partNumber: number; etag: string }[];
  try {
    parts = await sendParts(blob, plan.parts, name, turn);
  } catch (error) {
    // Never complete for bytes storage does not have: let the half-made upload go.
    void drive('drive.upload.direct.cancelBatch', { uploadId, batchId: batch.batchId }).catch(() => undefined);
    throw error;
  }
  turn.progress?.({ phase: 'saving' });
  const done = await drive<{ driveFiles?: { mediaAssetId?: string | null; url?: string | null }[] }>('drive.upload.direct.completeBatch', {
    uploadId,
    batchId: batch.batchId,
    parts: { [plan.sessionId]: parts },
  });
  const row = done?.driveFiles?.[0];
  if (!row?.mediaAssetId || !row.url) throw new Error(`${name} is in Flobi Drive but could not be placed. Place it from My Stuff in a moment.`);
  return { mediaAssetId: row.mediaAssetId, url: row.url };
}

/** The project's Drive folder, which Projects makes when it has none yet. Drive's top level when it cannot be had. */
async function projectFolder(projectId: string): Promise<string | null> {
  try {
    const response = await apiClient.post('/projects/project.driveFolder.get', { projectId });
    const answer = response?.data as { data?: { driveFolderId?: string | null }; driveFolderId?: string | null } | undefined;
    return answer?.data?.driveFolderId ?? answer?.driveFolderId ?? null;
  } catch (error) {
    // Better at Drive's top level than not at all.
    console.warn('The project’s Drive folder could not be had; the upload goes to Drive’s top level.', error);
    return null;
  }
}

/** Every part to its address, a few at a time; each part's ETag, in part order, as completeBatch takes them. */
async function sendParts(blob: Blob, plan: readonly PartPlan[], name: string, turn: Turn): Promise<{ partNumber: number; etag: string }[]> {
  const queue = [...plan];
  const done: { partNumber: number; etag: string }[] = [];
  let failed: unknown = null;
  const bytes = new Map<number, number>();
  const work = async () => {
    while (queue.length > 0 && !failed) {
      const part = queue.shift()!;
      try {
        const onBytes = turn.progress ? (loaded: number) => {
          bytes.set(part.partNumber, loaded); turn.moved();
          turn.progress!({ phase: 'uploading', loaded: [...bytes.values()].reduce((sum, value) => sum + value, 0), total: blob.size });
        } : undefined;
        done.push({ partNumber: part.partNumber, etag: await putPart(part.uploadUrl, blob.slice(part.start, part.end), name, turn.signal, onBytes) });
        turn.moved();
      } catch (error) {
        failed ??= error;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(PARALLEL, queue.length) }, work));
  if (failed) throw failed;
  return done.sort((a, b) => a.partNumber - b.partNumber);
}

/** One part, retried like Drive's app retries it: a failed send or a 5xx up to three times, a 4xx never. */
async function putPart(url: string, part: Blob, name: string, signal: AbortSignal, onBytes?: (loaded: number, total: number) => void): Promise<string> {
  let why = '';
  for (let attempt = 0; attempt < TRIES; attempt += 1) {
    if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
    let response: Awaited<ReturnType<typeof putStorage>>;
    try {
      response = await putStorage(url, part, signal, undefined, onBytes);
    } catch (error) {
      // Given up on: trying again would only be given up on again.
      if (signal.aborted) throw signal.reason;
      why = error instanceof Error ? error.message : 'storage could not be reached';
      continue;
    }
    if (response.ok) {
      const etag = response.etag.replace(/"/g, '');
      if (etag) return etag;
      throw new Error(`${name} could not be sent to Flobi Drive (storage sent no ETag). Nothing was saved.`);
    }
    why = `status ${response.status}`;
    if (response.status >= 400 && response.status < 500) break;
  }
  throw new Error(`${name} could not be sent to Flobi Drive (${why}). Nothing was saved.`);
}

/** A picture redrawn into another kind takes that kind's extension: a Drive file named .avif that is a WebP would mislead. */
function renamed(name: string, mime: string): string {
  const extension = mime === 'image/png' ? 'png' : mime === 'image/webp' ? 'webp' : null;
  return extension ? `${name.replace(/\.[^.]+$/, '')}.${extension}` : name;
}

/** The file's own media, through the Artwork backend: presigned, PUT straight to storage, registered. */
async function store(documentId: string, blob: Blob, name: string, mime: string, turn: Turn): Promise<{ mediaAssetId: string; url: string }> {
  const presigned = await artworkCmd<Presigned>('artwork.media.presign', {
    documentId,
    filename: name,
    mimeType: mime,
    sizeBytes: blob.size,
  });
  turn.moved();

  turn.progress?.({ phase: 'uploading', loaded: 0, total: blob.size });
  const put = await putStorage(presigned.uploadUrl, blob, turn.signal,
    { 'Content-Type': mime, ...(presigned.headers ?? {}) },
    turn.progress ? (loaded, total) => { turn.moved(); turn.progress!({ phase: 'uploading', loaded, total }); } : undefined);
  if (!put.ok) throw new Error(`Storage refused the upload (${put.status}).`);
  turn.moved();
  turn.progress?.({ phase: 'saving' });

  const asset = await artworkCmd<{ mediaAssetId: string; url: string | null }>('artwork.media.register', {
    documentId,
    storageRef: presigned.storageRef,
    storagePath: presigned.storagePath,
    mimeType: mime,
    sizeBytes: blob.size,
    filename: name,
  });

  // Without a URL from media-back the picture would only exist in this browser, which
  // is worse than saying so: the next person to open the file would see a hole.
  if (!asset.url) throw new Error('It was stored but has no address yet. Try again in a moment.');
  return { mediaAssetId: asset.mediaAssetId, url: asset.url };
}
